import { reactive, computed } from 'vue';

/**
 * LocalStorage keys
 */
const LS_KEYS = {
  SCREENED: '@screened-systems',
  LOADED: '@loaded-systems',
};

/**
 * Create a small state machine to manage screening + cross-system questions.
 * Only two systems exist: 0 = E-CAT, 1 = R-CAT.
 */
export function createSurveyMachine({ activeSystem }) {
  const state = reactive({
    /** Current high-level state */
    status: 'IDLE', // 'IDLE' | 'SCREENING_PENDING' | 'LOADING_SECONDARY' | 'READY'

    /** Which system is currently waiting for screening */
    pendingScreeningSystem: null, // number | null

    /** Sets */
    triggeredSystems: new Set(),
    screenedSystems: loadSet(LS_KEYS.SCREENED),
    loadedSystems: loadSet(LS_KEYS.LOADED),

    /** Active system you're in (1 = R-CAT, 0 = E-CAT) */
    activeSystem,
  });

  /** Persist sets whenever they change */
  function persist() {
    saveSet(LS_KEYS.SCREENED, state.screenedSystems);
    saveSet(LS_KEYS.LOADED, state.loadedSystems);
  }

  /**
   * Call after every change to *primary* questions. The argument is the new set of systems
   * currently triggered by selected responses in the PRIMARY system only.
   */
  function updateTriggers(newTriggeredSet) {
    // Diff
    const added = diffAdded(state.triggeredSystems, newTriggeredSet);
    const removed = diffAdded(newTriggeredSet, state.triggeredSystems);

    state.triggeredSystems = newTriggeredSet; // replace reference

    // Handle removed first (deselects): unload their questions if loaded
    if (removed.size) {
      removed.forEach((sys) => {
        state.loadedSystems.delete(sys);
        // NOTE: We purposely do NOT delete from screenedSystems here.
        // If you want to force re-screening on re-trigger later, clear it here.
      });
      persist();
    }

    // If any newly triggered system hasn't been screened yet, ask for screening
    const needsScreening = [...added].filter((s) => !state.screenedSystems.has(s));

    if (needsScreening.length > 0) {
      state.pendingScreeningSystem = needsScreening[0]; // first one
      state.status = 'SCREENING_PENDING';
      return;
    }

    // Otherwise, we can mark ourselves READY or IDLE depending on loads
    settleReadyOrIdle();
  }

  /** Call when the user CANCELS screening */
  function cancelScreening() {
    state.pendingScreeningSystem = null;
    // If nothing else is pending, fall back to IDLE/READY depending on loads
    settleReadyOrIdle();
  }

  /** Call when screening was CONFIRMED for `systemType` */
  function confirmScreening(systemType) {
    state.screenedSystems.add(systemType);
    state.pendingScreeningSystem = null;
    state.status = 'LOADING_SECONDARY';
    persist();
  }

  /** After we finish loading cross-system questions for `systemType` */
  function finishLoadingSecondary(systemType) {
    state.loadedSystems.add(systemType);
    persist();
    settleReadyOrIdle();
  }

  /** Remove all questions for specific systems (e.g., when deselected) */
  function unloadSystems(systems) {
    systems.forEach((s) => state.loadedSystems.delete(s));
    persist();
    settleReadyOrIdle();
  }

  /** Helper to decide between READY and IDLE */
  function settleReadyOrIdle() {
    if (state.pendingScreeningSystem != null) {
      state.status = 'SCREENING_PENDING';
      return;
    }
    if (state.loadedSystems.size > 0) {
      state.status = 'READY';
    } else {
      state.status = 'IDLE';
    }
  }

  const isReady = computed(() => state.status === 'READY');

  return {
    state,
    updateTriggers,
    cancelScreening,
    confirmScreening,
    finishLoadingSecondary,
    unloadSystems,
    isReady,
  };
}

// ---------- utils ----------
function diffAdded(oldSet, newSet) {
  const added = new Set();
  newSet.forEach((v) => {
    if (!oldSet.has(v)) added.add(v);
  });
  return added;
}

function loadSet(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return new Set();
    return new Set(JSON.parse(raw));
  } catch {
    return new Set();
  }
}

function saveSet(key, set) {
  localStorage.setItem(key, JSON.stringify([...set]));
}